(() => {
  // Check if already defined to prevent duplicate registration
  if (customElements.get('localization-form')) return;
  class LocalizationForm extends HTMLElement {
    constructor() {
      super();
      // Initialize elements
      this.elements = {
        input: this.querySelector('input[name="language_code"],input[name="country_code"]'),
        button: this.querySelector('button'),
        panel: this.querySelector('ul'),
        links: this.querySelectorAll('a')
      };
      // State management
      this.isOpen = false;
      this.isTouch = false;
      // Bind methods
      this.handleClick = this.handleClick.bind(this);
      this.handleTouchStart = this.handleTouchStart.bind(this);
      this.handleFocusOut = this.handleFocusOut.bind(this);
      this.handleKeyboard = this.handleKeyboard.bind(this);
      this.handleItemSelect = this.handleItemSelect.bind(this);
      this.handleClickOutside = this.handleClickOutside.bind(this);
      this.initializeAttributes();
      this.attachEventListeners();
    }
    initializeAttributes() {
      // Ensure ARIA attributes are set
      this.elements.button.setAttribute('aria-expanded', 'false');
      this.elements.button.setAttribute('aria-controls', 'localization-panel');
      this.elements.panel.id = 'localization-panel';
      this.elements.panel.setAttribute('role', 'listbox');
      this.elements.panel.setAttribute('hidden', '');
    }
    attachEventListeners() {
      // Button events
      this.elements.button.addEventListener('click', this.handleClick);
      this.elements.button.addEventListener('touchstart', this.handleTouchStart, { passive: true });
      // Panel events
      this.elements.panel.addEventListener('focusout', this.handleFocusOut);
      // Keyboard navigation
      this.addEventListener('keydown', this.handleKeyboard);
      // Click outside detection
      document.addEventListener('click', this.handleClickOutside);
      document.addEventListener('touchend', this.handleClickOutside);
      // Item selection
      this.elements.links.forEach(link => {
        link.setAttribute('role', 'option');
        link.addEventListener('click', this.handleItemSelect);
        link.addEventListener('touchend', (e) => {
          e.preventDefault();
          this.handleItemSelect(e);
        });
      });
    }
    handleClick(event) {
      // Prevent click handling if touch device
      if (this.isTouch) {
        this.isTouch = false;
        return;
      }
      event.preventDefault();
      this.togglePanel();
    }
    handleTouchStart(event) {
      this.isTouch = true;
      this.togglePanel();
    }
    handleFocusOut(event) {
      // Check if focus is still within the component
      if (!this.contains(event.relatedTarget)) {
        this.closePanel();
      }
    }
    handleKeyboard(event) {
      switch (event.key) {
        case 'Escape':
          if (this.isOpen) {
            event.preventDefault();
            this.closePanel();
            this.elements.button.focus();
          }
          break;
        case 'ArrowDown':
          if (this.isOpen) {
            event.preventDefault();
            this.focusNextItem();
          }
          break;
        case 'ArrowUp':
          if (this.isOpen) {
            event.preventDefault();
            this.focusPreviousItem();
          }
          break;
        case 'Enter':
        case ' ':
          if (document.activeElement !== this.elements.button) return;
          event.preventDefault();
          this.togglePanel();
          break;
      }
    }
    handleClickOutside(event) {
      if (!this.contains(event.target) && this.isOpen) {
        this.closePanel();
      }
    }
    handleItemSelect(event) {
      event.preventDefault();
      event.stopPropagation();
      const form = this.querySelector('form');
      this.elements.input.value = event.currentTarget.dataset.value;
      this.closePanel();
      if (form) {
        // Small delay to ensure panel closes smoothly
        setTimeout(() => form.submit(), 100);
      }
    }
    togglePanel() {
      this.isOpen ? this.closePanel() : this.openPanel();
    }
    openPanel() {
      this.isOpen = true;
      this.elements.panel.removeAttribute('hidden');
      this.elements.button.setAttribute('aria-expanded', 'true');
      // Focus first item
      const firstLink = this.elements.links[0];
      if (firstLink) {
        setTimeout(() => firstLink.focus(), 100);
      }
    }
    closePanel() {
      this.isOpen = false;
      this.elements.panel.setAttribute('hidden', '');
      this.elements.button.setAttribute('aria-expanded', 'false');
    }
    focusNextItem() {
      const currentIndex = Array.from(this.elements.links).indexOf(document.activeElement);
      const nextIndex = (currentIndex + 1) % this.elements.links.length;
      this.elements.links[nextIndex].focus();
    }
    focusPreviousItem() {
      const currentIndex = Array.from(this.elements.links).indexOf(document.activeElement);
      const prevIndex = (currentIndex - 1 + this.elements.links.length) % this.elements.links.length;
      this.elements.links[prevIndex].focus();
    }
    disconnectedCallback() {
      // Clean up event listeners
      document.removeEventListener('click', this.handleClickOutside);
      document.removeEventListener('touchend', this.handleClickOutside);
    }
  }
  customElements.define('localization-form', LocalizationForm);
})();